/**
 * Modulo per la gestione del form di accessibilità
 * Permette agli utenti (autenticati o meno) di inviare richieste di contatto per questioni di accessibilità
 */

import Page from "~/pages/Page";
import { getApi } from "~/auth";
import UI from "~/ui";
import $ from "jquery";
import { generaRecaptcha, recaptchaReady, recaptchaToken } from "~/recaptcha";

/**
 * Interfaccia per la risposta della chiamata API
 */
interface ApiResponse {
    success: boolean;
    responseText?: string;
    message?: string;
}

/**
 * Classe per la gestione della pagina Accessibilità
 */
class Accessibilita extends Page {

    private theForm: HTMLFormElement | null = null;
    private auth: any = null;
    private userInfo: any = null;
    private isUserAuthenticated: boolean = false;
    private recaptchaGenerated: boolean = false;

    constructor() {
        super("accessibilita", "Accessibilita");
    }

    /**
     * Inizializza la pagina
     */
    pageStart(): Promise<void> {
        this.auth = getApi();
        this.userInfo = this.auth.getCached("userInfo");
        this.isUserAuthenticated = this.userInfo !== null && this.userInfo !== undefined;

        this.theForm = document.getElementById('form-accessibilita') as HTMLFormElement;

        if (!this.theForm) {
            console.warn("Form accessibilita non trovato");
            return Promise.resolve();
        }

        // Inizializza il form con i dati dell'utente se loggato
        this.initFormAccessibilita();

        // Gestisce la submit del form
        this.initFormSubmit();

        // ✅ Intercetta l'apertura della modale per reinizializzare il form
        this.initModalOpenHandler();

        return Promise.resolve();
    }

    /**
     * ✅ Gestisce l'apertura della modale per reinizializzare il form
     */
    private initModalOpenHandler(): void {
        // Intercetta l'evento di apertura della modale
        $('#ModalAccessibilita').on('show.bs.modal', () => {
            if (!this.theForm) return;

            // Reinizializza il form ogni volta che si apre la modale
            this.resetFormState();
            this.initFormAccessibilita();

            // ✅ Genera il reCAPTCHA quando si apre la modale
            this.generateRecaptcha();
        });
    }

    /**
     * ✅ Genera il reCAPTCHA
     */
    private async generateRecaptcha(): Promise<void> {
        try {
            await recaptchaReady();

            // Genera il token reCAPTCHA con action "accessibilita"
            // Il token viene automaticamente salvato nell'input hidden #ggtoken
            await generaRecaptcha("accessibilita");

            this.recaptchaGenerated = true;
            console.info('reCAPTCHA generato per il form accessibilità');
        } catch (error) {
            console.error('Errore durante la generazione del reCAPTCHA:', error);
            this.recaptchaGenerated = false;
        }
    }

    /**
     * ✅ Resetta completamente lo stato del form
     */
    private resetFormState(): void {
        if (!this.theForm) return;

        // Rimuove la classe di validazione dal form
        this.theForm.classList.remove('was-validated');

        // Rimuove le classi di validazione dai singoli campi
        $(this.theForm).find("input, select, textarea").each(function () {
            $(this).removeClass("is-invalid is-valid");
        });

        // Nasconde i messaggi di errore
        $(".invalid-feedback").hide();
    }

    /**
     * Inizializza il form con i dati dell'utente autenticato (se presente)
     */
    private initFormAccessibilita(): void {
        if (this.isUserAuthenticated && this.userInfo) {
            // Precompila i campi con i dati dell'utente
            $("#nome-accessibilita").val(this.userInfo.nome || "");
            $("#cognome-accessibilita").val(this.userInfo.cognome || "");
            $("#email-accessibilita").val(this.userInfo.email || "");

            const telefono = this.getTelefonoFromUserInfo();
            if (telefono) {
                $("#telefono-accessibilita").val(telefono);
            }

            // ✅ Imposta il checkbox privacy come checked e disabilitato (non modificabile)
            $("#chkPrivacy-accessibilita")
                .prop("checked", true)
                .prop("disabled", true)
                .removeAttr("required");
        } else {
            // Aggiungi il required al checkbox privacy per utenti non autenticati
            const $chkPrivacy = $("#chkPrivacy-accessibilita");
            if (!$chkPrivacy.attr("required")) {
                $chkPrivacy
                    .prop("required", true)
                    .prop("disabled", false)
                    .prop("checked", false);
            }
        }
    }

    /**
     * ✅ Recupera il numero di telefono o cellulare dall'array contatti dell'utente
     * Priorità: Cellulare (CEL) > Telefono (TEL)
     */
    private getTelefonoFromUserInfo(): string | null {
        if (!this.userInfo || !this.userInfo.contatti || !Array.isArray(this.userInfo.contatti)) {
            return null;
        }

        // Cerca prima un cellulare (CEL)
        const cellulare = this.userInfo.contatti.find((c: any) =>
            c.tipoContatto === 'CELL' && c.valore
        );

        if (cellulare) {
            return this.formatTelefono(cellulare);
        }

        // Se non c'è cellulare, cerca un telefono (TEL)
        const telefono = this.userInfo.contatti.find((c: any) =>
            c.tipoContatto === 'TEL' && c.valore
        );

        if (telefono) {
            return this.formatTelefono(telefono);
        }

        return null;
    }

    /**
     * ✅ Formatta il numero di telefono con eventuale prefisso
     */
    private formatTelefono(contatto: any): string {
        if (!contatto || !contatto.valore) {
            return '';
        }

        // Se c'è un prefisso, lo concatena
        if (contatto.prefisso && contatto.prefisso.trim() !== '') {
            return `${contatto.prefisso} ${contatto.valore}`;
        }

        return contatto.valore;
    }

    /**
     * Inizializza la gestione della submit del form
     */
    private initFormSubmit(): void {
        if (!this.theForm) return;

        this.theForm.addEventListener('submit', async (event: Event) => {
            event.preventDefault();
            event.stopPropagation();

            if (this.theForm && this.theForm.checkValidity()) {
                await this.richiestaContattoAccessibilita();
            }else if (this.theForm) {
                this.theForm.classList.add('was-validated');
            }
        }, false);
    }

    /**
     * Invia la richiesta di contatto per accessibilità
     */
    private async richiestaContattoAccessibilita(): Promise<void> {
        if (!this.theForm) return;

        // ✅ Verifica il reCAPTCHA prima di procedere
        if (!this.recaptchaGenerated) {
            UI.confirmOneButton("", "Errore: reCAPTCHA non generato");
            return;
        }

        // ✅ Ottieni il token dal campo hidden
        const recaptchaTokenValue = recaptchaToken();

        if (!recaptchaTokenValue) {
            UI.confirmOneButton("", "Completa la verifica reCAPTCHA");
            return;
        }

        // Serializza i dati del form
        const formData: Record<string, string> = {};
        $(this.theForm)
            .serializeArray()
            .forEach((field) => {
                formData[field.name] = field.value;
            });

        // ✅ Aggiungi manualmente il valore del checkbox privacy se l'utente è autenticato
        // (i campi disabled non vengono serializzati)
        if (this.isUserAuthenticated) {
            formData["Privacy"] = "true";
        }

        // ✅ Il token reCAPTCHA è già incluso nel formData tramite l'input hidden "ggtoken"
        // ma lo sovrascriviamo per sicurezza
        formData["RecaptchaToken"] = recaptchaTokenValue;

        console.info(formData);

        this.spinner.spin();

        try {
            const url = `${window.BaseUrlWebApi}Mailing/RichiestaContattoAccessibilita`;

            const response = await fetch(url, {
                method: 'POST',
                headers: {
                    'Content-Type': 'application/json; charset=utf-8'
                },
                body: JSON.stringify(formData)
            });

            const data: ApiResponse = await response.json();

            if (data.success) {
                // Reset del form
                this.theForm.reset();

                // ✅ Usa il metodo centralizzato per resettare lo stato
                this.resetFormState();

                // Chiudi la modale se presente
                $('#ModalAccessibilita').modal('hide');

                // Mostra messaggio di successo
                UI.confirmOneButton(
                    "Richiesta inviata",
                    "E-mail correttamente inviata, sarete ricontattati prima possibile"
                );

                // ✅ Rigenera il reCAPTCHA per il prossimo utilizzo
                this.recaptchaGenerated = false;
            }
            else {
                const errorMessage = data.responseText || data.message || "Errore sconosciuto";
                $("#resultmessage").text(`Non è stato possibile inviare la E-mail (${errorMessage})`);
                UI.confirmOneButton("", `Errore: ${errorMessage}`);

                // ✅ Rigenera il reCAPTCHA in caso di errore
                this.resetFormState();
                await this.generateRecaptcha();
            }

        } catch (error: any) {
            console.error("Errore durante l'invio della richiesta:", error);
            const errorMessage = error.message || "Si è verificato un errore durante l'invio";
            $("#resultmessage").text(`Si è verificato un errore durante l'invio (${errorMessage})`);
            UI.confirmOneButton("", "Errore durante l'invio della richiesta");

            // ✅ Rigenera il reCAPTCHA in caso di errore
            await this.generateRecaptcha();
        } finally {
            this.spinner.stop();
        }
    }
}

export default Accessibilita;
